import React, { useCallback, useState } from 'react';
import { FavouriteToggleBase } from 'components/listing-card/favourite-toggle';
import ListingPrice, { ListingPriceData } from 'components/listing-price';
import { buildClassName } from 'utils/build-class-name';
import ListingImage from 'components/listing-image';
import getImageSrc from 'utils/get-image';
import styles from './style.module.scss';
import { listingCardIds, mapPageIds } from 'constants/test-constants';
import { LISTING_TYPE, MapListingCardType, TERMS_OF_USE_REQUIRED_TYPE } from './types';
import ListingBedBathParking from './listing-bed-bath-sqft-labels';
import { LoginRequired, TermsOfUseRequired } from './compliancy-disclaimer';
import { Provider, AreaPageListing_Status, MapPageListing } from '@zoocasa/go-search';
import generateFullAddress from 'utils/get-full-address';
import ProviderLabels from 'components/listing-card/provider-labels';
import { ComingSoonBadge } from 'ui/badges';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

//#region Types
export type MapListingCardProps = {
  type: MapListingCardType;
  id: number;
  imageUrl: string;
  showProviderLogo?: boolean;
  sqftLabel: string;
  listing: MapPageListing;
  onListingClick: (listing: MapPageListing) => void;
  onListingAddressClick?: (listing: MapPageListing) => void;
  /**
   * Called when the card is clicked while `type === 'terms-of-use-required'`.
   * When provided, this is invoked INSTEAD of `onListingClick` so the parent
   * can open the terms-of-use modal. Required because `MapListingCard` may be
   * rendered inside a separate React root (MapKit callouts), where the app's
   * `ModalContext.Provider` is not in scope.
   */
  onTermsRequiredClick?: (listing: MapPageListing) => void;
  isFavourite?: boolean;
  onFavouriteClick?: (id: number) => boolean;
  isExpTheme?: boolean;
  hideSquareFootage?: boolean;
  /** Custom class on the card root */
  className?: string;
};
//#endregion

//#region Helper Functions
function parseAddress(addressString: string) {
  const components = addressString.split(',').map(component => component.trim());
  const street = components[0] || '';
  const city = components[1] || '';
  const province = components[2] || '';
  const postalCode = components[3] || '';
  return {
    street,
    city,
    province,
    postalCode,
  };
}
//#endregion

export default function MapListingCard(
  { type,
    id,
    listing,
    imageUrl,
    sqftLabel,
    showProviderLogo = false,
    isFavourite = false,
    onListingClick,
    onFavouriteClick,
    onListingAddressClick,
    onTermsRequiredClick,
    isExpTheme,
    hideSquareFootage,
    className,
  }: MapListingCardProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const cardImageClass = buildClassName(styles.image, type !== LISTING_TYPE && styles['blur-image']);
  const streetAddressClassName = onListingAddressClick ? 'street-address-link' : 'street-address';
  const cardClassName = buildClassName(styles.component, isExpTheme && styles['exp-theme'], className);

  const address = parseAddress(listing.address);
  const fullAddress = `${generateFullAddress(address.street, address.city, address.province, false)}`;
  const [isFavouriteState, toggleFavouriteState] = useState(isFavourite);
  const formattedSqftLabel = hideSquareFootage ? '-' : sqftLabel;

  const handleOnCLick = useCallback(() => {
    // Terms-of-use cards open a modal that lives in the page-level React tree.
    // We delegate via a separate prop instead of calling useModalContext here
    // because MapListingCard is sometimes rendered inside a MapKit callout's
    // standalone React root that has no ModalContext.Provider in scope.
    if (type === TERMS_OF_USE_REQUIRED_TYPE && onTermsRequiredClick) {
      onTermsRequiredClick(listing);
      return;
    }
    onListingClick?.(listing);
  }, [listing, onListingClick, onTermsRequiredClick, type]);

  const handleListingAddressClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (onListingAddressClick) {
      event.stopPropagation(); // Prevent clicking the address from triggering the click handler from the card
      onListingAddressClick?.(listing);
    }
  }, [listing, onListingAddressClick]);

  const handleToggleFavouriteState = useCallback(() => {

    const res = onFavouriteClick?.(id);
    toggleFavouriteState(res);
  }, [id, onFavouriteClick]);

  const listingPriceData: ListingPriceData = {
    statusLabel: '', // Not Used
    isTreb: [Provider.PROVIDER_TREB_IDX, Provider.PROVIDER_TREB_VOW].includes(listing.providerId),
    isCrea: listing.providerId === Provider.PROVIDER_CREA_DDF,
    isItsoVow: false, // Not Used
    isPillar9Vow: false, // Not Used
    isRebgvVow: false, // Not Used
    providerId: listing.providerId,
    soldPrice: listing.soldPrice,
    isSoldWithoutSoldPrice: !listing.soldPrice && listing.statusId === AreaPageListing_Status.NotAvailableSold,
    price: listing.price,
    isPrivate: false, // Not Used
    agreementType: '', // Not Used
    isSignInAndTermsOfUseRequired: false, // Not Used
    province: address.province.toUpperCase(),
  };

  const imageAlt = t(LOCALE_COMMON_PATHS.LISTING_CARD_MEDIA_ALT, {
    alt: fullAddress || 'Listing',
    defaultValue: `${fullAddress || 'Listing'} | Card Image`,
  });

  return (
    <div className={cardClassName} onClick={handleOnCLick} data-testid={mapPageIds.listingsCardPopover}>
      <div className={styles['image-container']}>
        <ListingImage item={{ image: getImageSrc(imageUrl), alt: imageAlt, id: `${id}-card-image` }} className={cardImageClass} testId={listingCardIds.listingImage} />
        <ComingSoonBadge standardStatus={listing.standardStatus} />
      </div>
      <div className={styles.right}>
        { type === 'login-required' && 
           <LoginRequired /> }
        { type === 'terms-of-use-required' && 
           <TermsOfUseRequired /> }
        { type === 'listing' &&  
          <>
            <div className={styles['price-time']} data-testid={listingCardIds.listingPrice}>
              <ListingPrice listing={listingPriceData} isVisible={true} isMinimal={true} shouldScramble={false} isExpTheme={isExpTheme} />
              <div className={styles['favourite']}>
                <FavouriteToggleBase isFavourite={isFavouriteState} isSmallCard={true} toggleFavouriteState={handleToggleFavouriteState} />
              </div>
            </div>
            <div className={styles[streetAddressClassName]} onClick={handleListingAddressClick} data-testid={listingCardIds.listingAddress}>
              <p>{fullAddress}</p>
            </div>
            <ListingBedBathParking bathrooms={listing.bathrooms} bedrooms={listing.bedrooms} sqftLabel={formattedSqftLabel} />
            <div className={styles['provider-label']}>
              <ProviderLabels mlsNum={listing.mls} brokerage={listing.brokerage} isBlurred={false} hasLogo={showProviderLogo} />
            </div>
            <div className={styles['provider-logo-container']}>
              { showProviderLogo && 
              // eslint-disable-next-line @next/next/no-img-element
              <img className={styles['provider-logo']} 
                src={`https://zoocasa.s3.amazonaws.com/provider-logos/PROV_LOGO_${listing.providerId}.png`} 
                alt={t(LOCALE_COMMON_PATHS.LISTING_CARD_PROVIDER_LOGO_ALT, {
                  defaultValue: 'MLS logo',
                })} 
              /> }
            </div>
          </>
        }
      </div>
    </div>
  );
}
